import axios from 'axios';
import { useSelector } from 'react-redux'
import { useState, useCallback, useRef } from 'react';

const useFileUpload = (onUploadsComplete) => {

  const maxChunkSize = useSelector((state) => state.app.maxChunkSize)
  const CHUNK_SIZE = maxChunkSize * 1024; // Convert to bytes

  const [uploadProgress, setUploadProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [isPaused, setIsPaused] = useState(false); // Track pause state
  const abortControllerRef = useRef(null);
  const pauseRef = useRef(false); // Ref to control pause behavior

  const uploadChunk = async (batchId, file, chunk, chunkIndex, totalChunks, signal) => {
    const formData = new FormData();
    const { name, relativePath, size } = file;

    formData.append("fileChunk", chunk);
    formData.append("chunkIndex", chunkIndex);
    formData.append("totalChunks", totalChunks);
    formData.append("batchId", batchId);
    formData.append("fileName", name);
    formData.append('relativePath', relativePath);

    return axios.post('/api/wt/documents/uploadfile', formData, {
      signal,
      onUploadProgress: () => {
        setUploadProgress((prevProgress) => {
          const totalProgress = Math.round(((chunkIndex + 1) / totalChunks) * 100);

          return {
            ...prevProgress,
            [relativePath]: {
              totalProgress
            },
          };
        });
      },
    });
  };

  const uploadFile = async (batchId, file, signal) => {
    if (file.size === 0) {
      await uploadChunk(batchId, file, new Blob(), 0, 1, signal);
      return;
    }

    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      // Check for pause state
      while (pauseRef.current) {
        await new Promise((resolve) => setTimeout(resolve, 100)); // Wait for pause to be lifted
      }

      const start = chunkIndex * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      await uploadChunk(batchId, file, chunk, chunkIndex, totalChunks, signal);
    }
  };

  const uploadFiles = useCallback(async (batch) => {
    setIsUploading(true);
    pauseRef.current = false;
    abortControllerRef.current = new AbortController();

    const { files, batchId } = batch;
    const { signal } = abortControllerRef.current;

    abortControllerRef.current.batchId = batchId;

    try {
      for (let i = 0; i < files.length; i++) {
        await uploadFile(batchId, files[i], signal);
      }

      if (onUploadsComplete) {
        onUploadsComplete({ batchId: batch.batchId, ...batch.meta });
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log('Upload cancelled');
      } else {
        console.error('Error during file upload:', error);
      }
    } finally {
      setIsUploading(false);
    }
  }, [onUploadsComplete]);

  const cancelUpload = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      cancelCleanup(abortControllerRef.current.batchId);
      abortControllerRef.current = null;
    }
  }, []);

  const pauseUpload = useCallback(() => {
    setIsPaused(true);
    pauseRef.current = true;
  }, []);

  const resumeUpload = useCallback(() => {
    setIsPaused(false);
    pauseRef.current = false;
  }, []);

  const cancelCleanup = useCallback((batchId) => {
    axios.post('/api/wt/documents/cancelbatch', { batchId: batchId });
  }, []);

  return {
    uploadFiles,
    cancelUpload,
    pauseUpload,
    resumeUpload,
    uploadProgress,
    isUploading,
    isPaused,
    cancelCleanup
  };
};

export default useFileUpload;
